import type { MailAddress } from "@catmail/shared";
import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * 지금 쓰고 있는 주소 — **다중 계정의 "지금 계정"**이다.
 *
 * 주소 하나는 별칭이 아니라 완전한 신원이다(architecture.md §1). 수신도 발신도
 * 상대 목록도 주소마다 따로 있어서, 주소를 고르는 것은 걸러 보는 것이 아니라
 * **그 주소로 로그인한 것처럼 쓰는 것**에 가깝다. 그래서 방 목록·안 읽음·사람
 * 찾기·새 대화 발신 주소가 한꺼번에 따라간다.
 *
 * 고른 값은 **이 기기에 남는다.** 계정에 저장하지 않는 이유는 폰에서 개인 주소,
 * 노트북에서 업무 주소처럼 기기마다 쓰는 신원이 다르기 때문이다. 기기를 따라
 * 옮겨 다니면 폰을 열 때마다 업무 주소로 시작한다.
 *
 * 사람마다 따로 담는다. 한 브라우저를 둘이 쓸 때 앞사람이 고른 주소를 이어받으면
 * 남의 주소로 시작한다 — 게다가 그 id는 내 것이 아니라 아무것도 안 보인다.
 */

const STORAGE_PREFIX = "catmail.identity.";

function keyFor(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function readStored(userId: string): string | null {
  if (!userId) return null;
  try {
    return window.localStorage.getItem(keyFor(userId));
    // 사생활 보호 모드처럼 저장소를 못 쓰는 브라우저가 있다. 매번 기본값으로 간다.
  } catch {
    return null;
  }
}

/**
 * 고를 수 있는 주소만 남긴다.
 *
 * 아직 안 받아준 주소와 꺼둔 주소는 **갈아탈 자리가 아니다.** 갈아타 봐야 메일이
 * 안 들어오는 주소라 빈 목록만 보이고, 왜 비었는지는 화면 어디에도 안 적힌다.
 */
export function selectableAddresses(addresses: MailAddress[]): MailAddress[] {
  return addresses.filter(
    (entry) =>
      (entry.status ?? "approved") === "approved" && entry.enabled !== false,
  );
}

export function useCurrentIdentity(userId: string, addresses: MailAddress[]) {
  const [chosen, setChosen] = useState<string | null>(null);

  // 내 정보가 늦게 오므로 첫 렌더에는 사용자를 모른다. 알게 된 뒤 한 번 읽는다.
  useEffect(() => {
    setChosen(readStored(userId));
  }, [userId]);

  const options = useMemo(() => selectableAddresses(addresses), [addresses]);

  /**
   * 실제로 쓰는 주소.
   *
   * 저장된 것이 지금 목록에 없으면 — 지웠거나, 관리자가 껐거나, 남이 쓰던
   * 브라우저다 — **대표 주소로 되돌린다.** 없는 id를 그대로 보내면 서버가 404를
   * 주고 화면은 아무것도 못 그린다.
   */
  const current = useMemo(() => {
    const found = options.find((entry) => entry.id === chosen);
    if (found) return found;
    return options.find((entry) => entry.isPrimary) ?? options[0] ?? null;
  }, [options, chosen]);

  const select = useCallback(
    (identityId: string) => {
      setChosen(identityId);
      if (!userId) return;
      try {
        window.localStorage.setItem(keyFor(userId), identityId);
      } catch {
        // 저장 못 해도 이번 세션 동안은 고른 대로 쓴다.
      }
    },
    [userId],
  );

  return { current, options, select };
}
